import api from '../api/api';
import { logger } from '../utils/logger';

// Helper function to handle API errors
const handleApiError = (error) => {
  logger.error('Admin API Error:', error);
  
  if (error.response?.status === 401) {
    // Authentication error - clear admin tokens and redirect
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    window.location.href = '/admin/login';
    return {
      success: false,
      error: 'Authentication required. Please log in again.',
      authRequired: true
    };
  }
  
  return {
    success: false,
    error: error.response?.data?.error || 'An error occurred while processing your request'
  };
};

/**
 * Get dashboard statistics for admin
 * @returns {Promise} Dashboard statistics
 */
export const getDashboardStats = async () => {
  try {
    // Use api utility which handles admin token automatically
    const response = await api.get('/admin/dashboard');
    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get all users (candidates and employers) with pagination
 * @param {Object} params - Query parameters
 * @param {string} params.type - User type (all, candidates, employers)
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search term
 * @returns {Promise} Users list with pagination
 */
export const getAllUsers = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.type) queryParams.append('type', params.type);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    
    const response = await api.get(`/admin/users?${queryParams.toString()}`);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get user details
 * @param {string} id - User ID
 * @param {string} type - User type (candidate or employer)
 * @returns {Promise} User details
 */
export const getUserDetails = async (id, type) => {
  try {
    const response = await api.get(`/admin/users/${id}?type=${type}`);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update user status (active/inactive)
 * @param {string} id - User ID
 * @param {Object} data - Update data
 * @param {string} data.type - User type (candidate or employer)
 * @param {boolean} data.isActive - Active status
 * @returns {Promise} Updated user status
 */
export const updateUserStatus = async (id, data) => {
  try {
    const response = await api.put(`/admin/users/${id}/status`, data);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get revenue analytics
 * @param {Object} params - Query parameters
 * @param {string} params.period - Period (daily, monthly, yearly)
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @returns {Promise} Revenue analytics data
 */
export const getRevenueAnalytics = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    
    const response = await api.get(`/admin/revenue?${queryParams.toString()}`);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get tickets with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.status - Ticket status filter
 * @param {string} params.priority - Ticket priority filter
 * @param {string} params.category - Ticket category filter
 * @param {string} params.assignedTo - Assigned admin filter
 * @param {string} params.search - Search term
 * @returns {Promise} Tickets list with pagination
 */
export const getTickets = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.category) queryParams.append('category', params.category);
    if (params.assignedTo) queryParams.append('assignedTo', params.assignedTo);
    if (params.search) queryParams.append('search', params.search);
    
    const response = await api.get(`/admin/tickets?${queryParams.toString()}`);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get ticket details
 * @param {string} id - Ticket ID
 * @returns {Promise} Ticket details with comments
 */
export const getTicketDetails = async (id) => {
  try {
    const response = await api.get(`/admin/tickets/${id}`);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Create a new ticket
 * @param {Object} ticketData - Ticket data
 * @returns {Promise} Created ticket
 */
export const createTicket = async (ticketData) => {
  try {
    const response = await api.post(`/admin/tickets`, ticketData);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update ticket
 * @param {string} id - Ticket ID
 * @param {Object} ticketData - Ticket update data
 * @returns {Promise} Updated ticket
 */
export const updateTicket = async (id, ticketData) => {
  try {
    const response = await api.put(`/admin/tickets/${id}`, ticketData);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Add comment to ticket
 * @param {string} id - Ticket ID
 * @param {Object} commentData - Comment data
 * @param {string} commentData.text - Comment text
 * @returns {Promise} Created comment
 */
export const addTicketComment = async (id, commentData) => {
  try {
    const response = await api.post(`/admin/tickets/${id}/comments`, commentData);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get plan management data
 * @returns {Promise} Plan management data
 */
export const getPlanManagement = async () => {
  try {
    const response = await api.get(`/admin/plans`);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update employer plan
 * @param {string} id - Employer ID
 * @param {Object} planData - Plan update data
 * @param {string} planData.planId - Plan ID
 * @returns {Promise} Updated plan data
 */
export const updateEmployerPlan = async (id, planData) => {
  try {
    const response = await api.put(`/admin/employers/${id}/plan`, planData);

    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Admin login
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - Admin email
 * @param {string} credentials.password - Admin password
 * @returns {Promise} Login response with token
 */
export const adminLogin = async (credentials) => {
  try {
    // Use api utility but login doesn't need auth token
    const response = await api.post('/auth/admin/login', credentials);

    // Backend returns { success: true, data: { token, user } }
    // Store token in localStorage
    if (response.data.success && response.data.data?.token) {
      localStorage.setItem('adminToken', response.data.data.token);
    }
    
    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Admin logout
 * @returns {void}
 */
export const adminLogout = () => {
  localStorage.removeItem('adminToken');
  return {
    success: true
  };
};

export default {
  getDashboardStats,
  getAllUsers,
  getUserDetails,
  updateUserStatus,
  getRevenueAnalytics,
  getTickets,
  getTicketDetails,
  createTicket,
  updateTicket,
  addTicketComment,
  getPlanManagement,
  updateEmployerPlan,
  adminLogin,
  adminLogout
};
